"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertPropertiesToRegistration = exports.checkLandActivityType = exports.convertSegmentsToForestLandEnteringEtsSchema = void 0;
const helpers_1 = require("@turf/helpers");
const species_queries_1 = require("./models/species/species-queries");
const lease_expires_next_harvest_1 = require("./models/tupuake/properties/lease_expires_next_harvest");
const tupuake_conversion_1 = require("./tupuake-conversion");
const land_activity_type_1 = require("./models/tupuake/properties/land_activity_type");
const establishment_method_1 = require("./models/tupuake/properties/establishment_method");
const date_1 = require("./operations/date");
const forest_type_1 = require("./models/tupuake/properties/forest_type");
const shapefile_schema_1 = require("./models/shapefile_schema");
/**
 * https://carboncrop.atlassian.net/browse/CAR-4617
 *
 * In September 2023, the species code for european larch was different
 * between entering-forest-land (v7) and planting-and-clearing (v2) schema .
 * They differed by 1, and 195 and 196 are visually very similar, so it seemed
 * to be an error.
 * This was reported to MPI, and they acknowledged it and will probably fix it,
 * but until they do, we still want our system to work.
 *
 * @param caas
 */
const fixEuropeanLarch = (caas) => (0, helpers_1.featureCollection)(caas.features.map((f) => {
    if (f.properties.TREE_SPECI === 195) {
        return {
            ...f,
            properties: {
                ...f.properties,
                TREE_SPECI: 196,
            },
        };
    }
    return f;
}));
/**
 * Create a shapefile for submission to Tupu-Ake
 * to register new CAA's.
 *
 * Assumes that all passed in features are intended to be exported
 * (ie does not check for inclusion or report category)
 *
 * @param segments the segments to convert
 * @param options options for the conversion
 * @param custom_species species data for custom species
 * @returns the shapefile formatted as GEOJSON
 */
const convertSegmentsToForestLandEnteringEtsSchema = (segments, options = {}, custom_species = {}) => {
    (0, exports.checkLandActivityType)(segments);
    const outData = (0, helpers_1.featureCollection)(segments.features.map((f, index) => {
        try {
            const properties = (0, exports.convertPropertiesToRegistration)(f.properties, index, options, custom_species);
            return {
                ...f,
                properties,
            };
        }
        catch (e) {
            throw new Error(`Error converting segment ${f.id} (CAA ${f.properties.ets_caa_number}): ${String(e)}`);
        }
    }));
    const fixedData = fixEuropeanLarch(outData);
    return fixedData;
};
exports.convertSegmentsToForestLandEnteringEtsSchema = convertSegmentsToForestLandEnteringEtsSchema;
const checkLandActivityType = (segments) => {
    const features = segments.features.filter((f) => f.properties.land_activity_type !== undefined);
    const idx = features.findIndex((f) => f.properties.land_activity_type !== features[0].properties.land_activity_type);
    if (idx > 0) {
        throw new shapefile_schema_1.ConstraintError(`CAA (${features[idx].properties.ets_caa_number}) has a different land activity type with the CAA (${features[0].properties.ets_caa_number})`);
    }
};
exports.checkLandActivityType = checkLandActivityType;
const convertPropertiesToRegistration = (properties, sid, options = {}, custom_species = {}) => {
    var _a, _b;
    const species = requireField(properties, 'species');
    let species_id_data;
    if ((0, species_queries_1.isCustomSpecies)(species)) {
        const custom_species_id = species.split(':', 2)[1];
        if (!(custom_species_id in custom_species)) {
            throw new Error(`Custom species data for ${species} is required`);
        }
        species_id_data = custom_species[custom_species_id];
    }
    else {
        const etsSpecies = (0, species_queries_1.getETSSpecies)(species);
        if (etsSpecies === undefined) {
            throw new Error(`Could not find ETS species`);
        }
        species_id_data = {
            forest_type_id: (0, forest_type_1.getForestTypeByKey)(etsSpecies).tupuake_forest_type_id,
            predominant_species_id: (0, tupuake_conversion_1.getPredominantSpecieIdOrThrowError)(species),
            tree_species_id: (0, tupuake_conversion_1.getTreeSpecieIdOrThrowError)(species),
        };
    }
    // @ts-ignore-next-line We don't support the other category yet, but this should be checked before export to avoid exporting invalid shapefiles
    if (species_id_data.predominant_species_id === 13) {
        throw new shapefile_schema_1.ConstraintError('Predominant Species "other" not yet supported');
    }
    if (species_id_data.tree_species_id === 13) {
        throw new shapefile_schema_1.ConstraintError('Tree Species "other" not yet supported');
    }
    const props = {
        SID: sid,
        GLOBALID: null,
        CAA_NUMBER: requireField(properties, 'ets_caa_number').toString(),
        ESTABLISHM: (0, establishment_method_1.getEstablishmentMethodByKey)(requireField(properties, 'establishment_method'))
            .tupuake_id,
        FOREST_TYP: species_id_data.forest_type_id,
        START_DATE: (0, date_1.getNearlyStartOfYear)(requireField(properties, 'established_date')),
        END_DATE: getEndDate(properties, (_a = options.today) !== null && _a !== void 0 ? _a : new Date()),
        YEAR_FIRST: getYearFirst(properties),
        LAND_ACTIV: (0, land_activity_type_1.getLandActivityTypeByKey)(requireField(properties, 'land_activity_type')).tupuake_id,
        PREDOMINAN: species_id_data.predominant_species_id,
        OTHER_PRED: null,
        TREE_SPECI: species_id_data.tree_species_id,
        OTHER_TREE: null,
        STEM_COUNT: (_b = properties.stem_count) !== null && _b !== void 0 ? _b : null,
        Q_LAND_ACT: getQLandAct(properties),
        RIGHTS_LEA: getRightsLease(properties),
        GFF_EXPIRY: properties.grant_fund_expiry_date
            ? (0, date_1.formatDate)(properties.grant_fund_expiry_date)
            : null,
        COMPARTMEN: null,
        FOREST_NUM: null,
    };
    return props;
};
exports.convertPropertiesToRegistration = convertPropertiesToRegistration;
/**
 * Looks for a field in an object. If it is not there, throws an error
 *
 * @param props object to search
 * @param name name of the field to look for
 * @returns the value of the field
 */
const requireField = (props, name) => {
    const fieldValue = props[name];
    if (!fieldValue) {
        throw new shapefile_schema_1.MissingFieldError(name.toString());
    }
    return fieldValue;
};
/**
 * If there is a previous establishment date use that. Otherwise use the
 * current establishment date
 *
 * @param properties the properties of the input data
 * @returns the year of the first planting
 */
const getYearFirst = (properties) => {
    const dateFirst = properties.previous_established_date
        ? properties.previous_established_date
        : requireField(properties, 'established_date');
    return (0, date_1.yearNumber)(dateFirst);
};
/**
 * The date the planting was finished and ensures it isn't in the future
 *
 * @param properties the properties of the input data
 * @param today the date to use as "today"
 * @returns the end date
 */
const getEndDate = (properties, today) => {
    const maybePlantEndDate = (0, date_1.getNearlyEndOfYear)(requireField(properties, 'established_date'));
    if (new Date(maybePlantEndDate) > today) {
        return (0, date_1.formatDate)(today.toISOString());
    }
    return maybePlantEndDate;
};
/**
 * If the land activity type is holder_of_lease, then the lease expires next harvest is required.
 * If not we can use not_applicable
 *
 * @param properties the properties of the input data
 * @returns the lease expires next harvest
 */
const getQLandAct = (properties) => {
    const landActivityType = requireField(properties, 'land_activity_type');
    if (landActivityType === 'holder_of_lease') {
        if (properties.lease_expires_next_harvest === undefined) {
            return (0, lease_expires_next_harvest_1.getLeaseExpiresNextHarvestByKey)('no').tupuake_id;
        }
        if (properties.lease_expires_next_harvest === 'not_applicable') {
            throw new shapefile_schema_1.ConstraintError('lease_expires_next_harvest cannot be "not_applicable" when land_activity_type is "holder_of_lease"');
        }
        return (0, lease_expires_next_harvest_1.getLeaseExpiresNextHarvestByKey)(properties.lease_expires_next_harvest).tupuake_id;
    }
    return (0, lease_expires_next_harvest_1.getLeaseExpiresNextHarvestByKey)('not_applicable').tupuake_id;
};
/**
 * If the land activity type is holder_of_lease, then the lease expiry date is required.
 * If not we can use null
 *
 * @param properties the properties of the input data
 * @returns the lease expiry date
 */
const getRightsLease = (properties) => {
    const landActivityType = requireField(properties, 'land_activity_type');
    if (landActivityType === 'holder_of_lease') {
        return (0, date_1.formatDate)(requireField(properties, 'lease_expiry_date'));
    }
    return null;
};
